import type { VertexProvenance } from "../../wasm/editOps.ts";
import { identityProvenance } from "../provenance.ts";
import {
  identityRemap,
  numEdgeKey,
  rebuildTopologyEdgesPreservingWire,
} from "../topology.ts";
import type { PolygonTopology, RenderTriangleIndices } from "../types.ts";
import { EDGE_WIRE } from "../types.ts";

/**
 * Check whether an edge already exists between two vertices.
 */
export function edgeExists(
  indices: RenderTriangleIndices,
  v0: number,
  v1: number,
): boolean {
  const key = numEdgeKey(v0, v1);
  for (let i = 0; i < indices.length; i += 3) {
    const a = indices[i],
      b = indices[i + 1],
      c = indices[i + 2];
    if (
      numEdgeKey(a, b) === key ||
      numEdgeKey(b, c) === key ||
      numEdgeKey(c, a) === key
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Bridge two vertices by creating a wire edge between them.
 * Rebuilds the edge arrays to include the new wire edge while preserving
 * any pre-existing wire edges from the source topology.
 *
 * Vertex layout is unchanged — provenance is identity. Returned in the
 * canonical `OpResult` shape so the caller can pipe straight into
 * `applyResult` without per-op wrapping.
 */
export function bridgeVertices(
  vertices: Float32Array,
  topology: PolygonTopology,
  v0: number,
  v1: number,
): {
  vertices: Float32Array;
  topology: PolygonTopology;
  provenance: VertexProvenance;
} {
  // Rebuild the topology, preserving existing wire edges + every per-edge
  // attribute the source carries (flags, crease weights).
  const baseTopology = rebuildTopologyEdgesPreservingWire(
    topology.offsets,
    topology.corners,
    topology,
    identityRemap,
  );

  const provenance = identityProvenance(vertices.length / 3);

  // If the bridged edge already exists (face-bound or pre-existing wire)
  // there's nothing to add.
  const lo = Math.min(v0, v1),
    hi = Math.max(v0, v1);
  const newKey = numEdgeKey(lo, hi);
  const { edges: baseEdges } = baseTopology;
  for (let i = 0; i < baseEdges.length; i += 2) {
    if (numEdgeKey(baseEdges[i], baseEdges[i + 1]) === newKey) {
      return { vertices, topology: baseTopology, provenance };
    }
  }

  // Append the new bridge edge as a wire edge. Extend every parallel
  // per-edge array so the new index has a well-defined default:
  //   - edgeFlags: EDGE_WIRE
  //   - edgeCreases (if present): 0 (fresh geometry is not creased)
  const oldEdgeCount = baseEdges.length / 2;
  const newEdges = new Uint32Array(baseEdges.length + 2);
  newEdges.set(baseEdges);
  newEdges[baseEdges.length] = lo;
  newEdges[baseEdges.length + 1] = hi;

  const newFlags = new Uint8Array(oldEdgeCount + 1);
  newFlags.set(baseTopology.edgeFlags);
  newFlags[oldEdgeCount] = EDGE_WIRE;

  let newCreases: Float32Array | undefined;
  if (baseTopology.edgeCreases) {
    newCreases = new Float32Array(oldEdgeCount + 1);
    newCreases.set(baseTopology.edgeCreases);
    // newCreases[oldEdgeCount] defaults to 0 — fresh bridge edge is smooth.
  }

  return {
    vertices,
    topology: {
      ...baseTopology,
      edges: newEdges,
      edgeFlags: newFlags,
      ...(newCreases ? { edgeCreases: newCreases } : {}),
    },
    provenance,
  };
}
